import React, { useEffect, useState, useCallback } from "react";
import { Spin, Alert } from "antd";
import {
  HourglassEmpty, Autorenew, CheckCircle, Cancel, AccessTime,
} from "@mui/icons-material";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsSettlements } from "../../utils/ApiEndpoints";
import {
  PageContainer, StatsGrid, StatCard, StatLabel, StatValue, StatSub,
  IconBox, Card, SectionTitle, GRADIENTS, fmtRupees, fmtNum,
} from "../paUtils";
import styled from "styled-components";

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const StatusCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  &:last-child { border-bottom: none; }
`;

const STATUS_CONFIG = {
  Pending: { icon: <HourglassEmpty />, gradient: GRADIENTS.amber },
  Processing: { icon: <Autorenew />, gradient: GRADIENTS.blue },
  Settled: { icon: <CheckCircle />, gradient: GRADIENTS.green },
  Failed: { icon: <Cancel />, gradient: GRADIENTS.red },
};

function SettlementsAnalyticsTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsSettlements, {
        params: { startDate, endDate },
      });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load settlements data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <PageContainer>
      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <Spin spinning={loading}>
        {data && (
          <>
            <SectionTitle>By Status</SectionTitle>
            <StatusGrid>
              {Object.entries(data.byStatus || {}).map(([status, info]) => {
                const cfg = STATUS_CONFIG[status] || { icon: <CheckCircle />, gradient: GRADIENTS.purple };
                return (
                  <StatusCard key={status}>
                    <IconBox $bg={cfg.gradient}>{cfg.icon}</IconBox>
                    <StatLabel>{status}</StatLabel>
                    <StatValue style={{ fontSize: 22 }}>{fmtRupees(info.amount)}</StatValue>
                    <StatSub>{fmtNum(info.count)} settlements · TDS: {fmtRupees(info.tds)}</StatSub>
                  </StatusCard>
                );
              })}
            </StatusGrid>

            <Card>
              <SectionTitle>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <AccessTime style={{ fontSize: 18, color: "#6366f1" }} />
                  Settlement Time
                </span>
              </SectionTitle>
              <StatsGrid style={{ gridTemplateColumns: "repeat(3, 1fr)", maxWidth: 600 }}>
                <StatCard>
                  <StatLabel>Average</StatLabel>
                  <StatValue>{data.avgSettlementTime?.avgDays?.toFixed(1) ?? "—"} days</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>Fastest</StatLabel>
                  <StatValue>{data.avgSettlementTime?.minDays ?? "—"} days</StatValue>
                </StatCard>
                <StatCard>
                  <StatLabel>Slowest</StatLabel>
                  <StatValue>{data.avgSettlementTime?.maxDays ?? "—"} days</StatValue>
                </StatCard>
              </StatsGrid>
            </Card>

            <Card>
              <SectionTitle>Summary</SectionTitle>
              <SummaryRow>
                <span style={{ color: "#64748b" }}>Total Payout</span>
                <span style={{ fontWeight: 700, color: "#10b981", fontSize: 16 }}>{fmtRupees(data.summary?.totalPayout)}</span>
              </SummaryRow>
              <SummaryRow>
                <span style={{ color: "#64748b" }}>Total TDS</span>
                <span style={{ fontWeight: 600 }}>{fmtRupees(data.summary?.totalTds)}</span>
              </SummaryRow>
              <SummaryRow>
                <span style={{ color: "#64748b" }}>Total After TDS</span>
                <span style={{ fontWeight: 700, color: "#6366f1", fontSize: 16 }}>{fmtRupees(data.summary?.totalAfterTds)}</span>
              </SummaryRow>
              <SummaryRow>
                <span style={{ color: "#64748b" }}>Total Orders</span>
                <span style={{ fontWeight: 600 }}>{fmtNum(data.summary?.totalOrders)}</span>
              </SummaryRow>
            </Card>
          </>
        )}
      </Spin>
    </PageContainer>
  );
}

export default SettlementsAnalyticsTab;
